import * as ReactRouter from 'react-router-dom';
import React from 'react';
import styled from 'styled-components';
import { ErrorBoundary } from '@sentry/react';

import { AdminSideNav } from 'src/presentation/components/nav/AdminSideNav';
import AnswerLanguages from 'src/presentation/components/pages/answers/AnswerLanguages';
import appFeaturesResource from 'src/actions/auth/appFeaturesResource';
import Audiences from 'src/presentation/components/pages/audiences/Audiences';
import { ContentContainer } from 'src/presentation/components/layouts/Layout';
import customChannelsConfigurationResource from 'src/actions/channels/customChannelsConfigurationResource';
import ErrorFallback from 'src/presentation/components/pages/shared/ErrorFallback';
import history from 'src/configuration/history';
import PageRoute from 'src/presentation/components/pages/shared/PageRoute';
import sidekickConfigurationResource, { SidekickConfig } from 'src/actions/sidekick/sidekickConfigurationResource';
import TopBar from 'src/presentation/components/TopBar';
import useCustomChannelsConfiguration from 'src/presentation/components/pages/shared/hooks/useCustomChannelsConfiguration';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useSidekickV1Conf from './pages/sidekick/threads/hooks/useSidekickConf';

// Pages
import AgentStatusReasons from 'src/presentation/components/pages/agentStatus/AgentStatusReasons';
import ApiTokens from 'src/presentation/components/pages/integrations/apiTokens/ApiTokens';
import BusinessHours from 'src/presentation/components/pages/businessHours/BusinessHours';
import CompleteLoadingTransition from 'src/presentation/components/CompleteLoadingTransition';
import ComplianceEvents from 'src/presentation/components/pages/compliance/ComplianceEvents';
import CustomAttributes from 'src/presentation/components/pages/customAttributes/components/CustomAttributes';
import CustomChannels from 'src/presentation/components//pages/channels/customChannels/CustomChannels';
import DataRetentionConfiguration from 'src/presentation/components/pages/compliance/DataRetentionConfiguration';
import EmailRecipientCollections from 'src/presentation/components/pages/proactiveCommunications/components/proactiveEmail/EmailRecipientCollections';
import EmailSetup from 'src/presentation/components/pages/email/EmailSetup';
import EntryPoints from 'src/presentation/components/pages/channels/entryPoints/EntryPoints';
import FacebookConfigurations from 'src/presentation/components/pages/social/FacebookConfigurations';
import ChatConfigurations from 'src/presentation/components/pages/chat/ChatConfigurations';
import HelpCenter from 'src/presentation/components/pages/helpCenter/HelpCenter';
import HelpCenterSections from 'src/presentation/components/pages/helpCenter/HelpCenterSections';
import Home from 'src/presentation/components/pages/home/Home';
import IdleTimingConfig from 'src/presentation/components/pages/idleTiming/IdleTimingConfig';
import Imitate from 'src/presentation/components/pages/imitate/Imitate';
import Inboxes from 'src/presentation/components/pages/inboxes/Inboxes';
import InstagramConfigurations from 'src/presentation/components/pages/social/InstagramConfigurations';
import Integrations from 'src/presentation/components/pages/integrations/Integrations';
import IvrConfigurations from 'src/presentation/components/pages/ivr/IvrConfigurations';
import IvrLogsConfigurations from 'src/presentation/components/pages/ivrLogs/IvrLogsConfiguration';
import KeywordGroups from 'src/presentation/components/pages/answers/KeywordGroups';
import ActionAdapters from 'src/presentation/components/pages/glados/actionAdapters/ActionAdapters';
import LookupDebuggingHome from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/LookupDebuggingHome';
import MobileNumberIdentification from 'src/presentation/components/pages/mobileNumberIdentification/MobileNumberIdentification';
import ProactiveChatConfigurations from 'src/presentation/components/pages/proactiveChat/ChatCampaignConfiguration';
import ProactiveEmail from 'src/presentation/components/pages/proactiveCommunications/ProactiveEmail';
import ProactiveSms from 'src/presentation/components/pages/proactiveCommunications/ProactiveSms';
import ProactiveVoice from 'src/presentation/components/pages/proactiveCommunications/ProactiveVoice';
import RecipientIds from 'src/presentation/components/pages/channels/customChannels/CustomChannelRecipients';
import RelationshipLabels from 'src/presentation/components/pages/relationshipLabels/RelationshipLabels';
import Roles from 'src/presentation/components/pages/people/roles/Roles';
import Routing from 'src/presentation/components/pages/routing/Routing';
import Rules from 'src/presentation/components/pages/rules/Rules';
import ScheduledReports from 'src/presentation/components/pages/scheduledReports/ScheduledReports';
import SidekickThreads from 'src/presentation/components/pages/sidekick/threads/SidekickThreads';
import Skills from 'src/presentation/components/pages/skills/Skills';
import SLABusinessHours from 'src/presentation/components/pages/slaBusinessHours/SLABusinessHours';
import SmartMatchRules from 'src/presentation/components/pages/rules/SmartMatchRules';
import Sms from 'src/presentation/components/pages/channels/sms/Sms';
import SmsRecipientCollections from 'src/presentation/components/pages/proactiveCommunications/components/proactiveSms/SmsRecipientCollections';
import Sso from 'src/presentation/components/pages/integrations/sso/Sso';
import Teams from 'src/presentation/components/pages/people/teams/Teams';
import Threads from 'src/presentation/components/pages/threads/Threads';
import Topics from 'src/presentation/components/pages/topics/Topics';
import Users from 'src/presentation/components/pages/people/users/Users';
import Voice from 'src/presentation/components/pages/channels/voice/Voice';
import VoiceRecipientCollections from 'src/presentation/components/pages/proactiveCommunications/components/proactiveVoice/VoiceRecipientCollections';
import Webhooks from 'src/presentation/components/pages/integrations/webhooks/Webhooks';
import WhatsAppSenders from 'src/presentation/components/pages/channels/whatsapp/WhatsAppSenders';
const { Router, Route, Switch, Redirect } = ReactRouter;

function AppRoutes({ auth }) {
  const renderRedirect = () => {
    return (
      <Route>
        <Redirect exact from="/" to="/home" />
      </Route>
    );
  };

  const { loadingState: appFeaturesLoaded } = useDocumentResource(appFeaturesResource, { fetch: !!auth.userId });
  const { loadingState: customChannelsConfigLoading } = useDocumentResource(customChannelsConfigurationResource, {
    fetch: !!auth.userId,
  });
  const { loadingState: sidekickConfigLoading } = useDocumentResource<SidekickConfig>(sidekickConfigurationResource, {
    fetch: !!auth.userId,
  });

  const { isFeatureEnabled: isCustomChannelsEnabled } = useCustomChannelsConfiguration();
  const { isSidekickThreadsEnabled } = useSidekickV1Conf();

  const shouldShowFullApp =
    appFeaturesLoaded === 'succeeded' &&
    customChannelsConfigLoading === 'succeeded' &&
    sidekickConfigLoading === 'succeeded';

  const isFeatureEnabled = useIsFeatureEnabled();
  const hideSearch: boolean = !isFeatureEnabled('conversation:read');
  const showSupportChat: boolean = isFeatureEnabled('gladlySupportChat');

  return (
    <>
      {/* @ts-ignore */}
      <ErrorBoundary fallback={ErrorFallback}>
        {appFeaturesLoaded === 'loading' ? null : <TopBar hideSearch={hideSearch} showSupportChat={showSupportChat} />}
      </ErrorBoundary>
      <ContentContainer>
        <ContentCenterer>
          <CompleteLoadingTransition appFeaturesLoaded={appFeaturesLoaded} />
          {shouldShowFullApp ? (
            <React.Fragment>
              <Router history={history}>
                <AdminSideNav />
                <Switch>
                  <PageRoute page={Topics} path="/topics" roleFeature="topics:admin" />
                  <PageRoute
                    page={CustomAttributes}
                    path="/freeform-topics"
                    roleFeature="customAttributeManagement"
                    featureFlag="customAttributes"
                  />
                  <PageRoute page={AgentStatusReasons} path="/agent-status" roleFeature="agentStatusManagement" />
                  <PageRoute page={Rules} path="/rules" roleFeature="rulesManagement" />
                  <PageRoute page={BusinessHours} path="/business-hours" roleFeature="rulesManagement" />
                  <PageRoute
                    page={MobileNumberIdentification}
                    path="/mobile-number-identification"
                    roleFeature="mobileNumberLookupManagement"
                  />
                  <PageRoute page={SmartMatchRules} path="/people-match" roleFeature="rulesManagement" />
                  <PageRoute page={EmailSetup} path="/email" roleFeature="emailDomainsManagement" />
                  <PageRoute
                    page={RelationshipLabels}
                    path="/relationship-labels"
                    roleFeature="relationshipLabelsManagement"
                  />
                  <PageRoute page={FacebookConfigurations} path={`/facebook`} roleFeature="facebookAdminSettings" />
                  <PageRoute page={InstagramConfigurations} path={`/instagram`} roleFeature="facebookAdminSettings" />
                  <PageRoute page={IdleTimingConfig} path="/idle-timing" roleFeature="agentConfigurationManagement" />
                  <PageRoute page={AnswerLanguages} path="/answer-languages" roleFeature="answers:admin" />
                  <PageRoute page={Audiences} path="/audiences" roleFeature="answers:admin" />
                  {!isFeatureEnabled('disableKeywordGroup') && (
                    <PageRoute page={KeywordGroups} path="/keyword-groups" roleFeature="answers:admin" />
                  )}
                  <PageRoute page={ChatConfigurations} path="/chat" roleFeature="sidekickConfigurationManagement" />
                  <PageRoute page={IvrConfigurations} path="/ivrs" roleFeature="ivrConfigurationManagement" />
                  <PageRoute page={Voice} path="/voice" roleFeature="channelManagement" />
                  <PageRoute
                    page={VoiceRecipientCollections}
                    path="/proactive-voice/:campaignId/recipientcollections"
                    roleFeature="campaignsManagement"
                  />
                  <PageRoute
                    page={SmsRecipientCollections}
                    path="/proactive-sms/:campaignId/recipientcollections"
                    roleFeature="campaignsManagement"
                  />
                  <PageRoute
                    page={EmailRecipientCollections}
                    path="/proactive-email/:campaignId/recipientcollections"
                    roleFeature="campaignsManagement"
                  />
                  <PageRoute page={ProactiveEmail} path="/proactive-email" roleFeature="campaignsManagement" />
                  <PageRoute page={ProactiveSms} path="/proactive-sms" roleFeature="campaignsManagement" />
                  <PageRoute page={ProactiveVoice} path="/proactive-voice" roleFeature="campaignsManagement" />
                  {isCustomChannelsEnabled && (
                    <PageRoute
                      page={RecipientIds}
                      path="/custom-channels/:customChannelId/recipients"
                      roleFeature="channelManagement"
                    />
                  )}
                  {isCustomChannelsEnabled && (
                    <PageRoute page={CustomChannels} path="/custom-channels" roleFeature="channelManagement" />
                  )}
                  <PageRoute
                    page={HelpCenterSections}
                    path="/help-center/:configId/sections"
                    roleFeature="answers:admin"
                  />
                  <PageRoute page={Home} path="/home" roleFeature="" />
                  <PageRoute page={HelpCenter} path="/help-center" roleFeature="answers:admin" />
                  <PageRoute
                    page={ScheduledReports}
                    path="/scheduled-reports"
                    roleFeature="scheduledReportsManagement"
                  />
                  <PageRoute
                    page={IvrLogsConfigurations}
                    path="/ivr-sessions"
                    roleFeature="ivrConfigurationManagement"
                  />
                  <PageRoute
                    page={ProactiveChatConfigurations}
                    path="/proactive-chat"
                    roleFeature="campaignsManagement"
                  />
                  <PageRoute page={Integrations} path="/apps" roleFeature="integrationsManagement" />
                  <PageRoute page={LookupDebuggingHome} path="/lookupDebugger" roleFeature="debuggingView" />
                  <PageRoute page={Teams} path="/teams" roleFeature="teamManagement" />
                  <PageRoute page={ApiTokens} path="/api-tokens" roleFeature="devManagement" />
                  <PageRoute page={Webhooks} path="/webhooks" roleFeature="devManagement" />
                  <PageRoute page={Sso} path="/sso" roleFeature="ssoConfigurationManagement" />
                  <PageRoute page={Inboxes} path="/inboxes" roleFeature="inboxManagement" />
                  <PageRoute page={Users} path="/users" roleFeature="teamManagement" />
                  <PageRoute page={Roles} path="/roles" roleFeature="roleManagement" />
                  <PageRoute page={Threads} path="/threads" roleFeature="sidekickConfigurationManagement" />
                  <PageRoute page={Skills} path="/skills" roleFeature="skillManagement" />
                  <PageRoute
                    page={DataRetentionConfiguration}
                    path="/data-retention"
                    roleFeature="complianceManagement"
                  />
                  <PageRoute page={ComplianceEvents} path="/compliance-events" roleFeature="complianceManagement" />
                  <PageRoute page={Sms} path="/sms" roleFeature="channelManagement" />
                  <PageRoute page={Routing} path="/routing" roleFeature="channelManagement" />
                  <PageRoute page={EntryPoints} path="/entry-points" roleFeature="channelManagement" />
                  <PageRoute page={SLABusinessHours} path="/sla-business-hours" roleFeature="rulesManagement" />
                  {isSidekickThreadsEnabled && <PageRoute page={SidekickThreads} path="/sidekick-threads" />}
                  <PageRoute page={WhatsAppSenders} path="/whatsapp" roleFeature="channelManagement" />

                  <PageRoute
                    page={ActionAdapters}
                    path="/app-actions"
                    roleFeature="devManagement"
                    featureFlag="showLasagnaPage"
                  />

                  {renderRedirect()}
                </Switch>
              </Router>
            </React.Fragment>
          ) : (
            <Router history={history}>
              <PageRoute page={Imitate} path="/imitate" roleFeature="" />
            </Router>
          )}
        </ContentCenterer>
      </ContentContainer>
    </>
  );
}

const ContentCenterer = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: 1496px;
`;

export default React.memo(AppRoutes);
